import React from "react";
import { useForm } from "react-hook-form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Plus, MoreVertical, Search, Pencil, Trash2, CheckCircle, CircleAlert  } from "lucide-react";
import { useSuppliers } from "@/hooks/useSuppliers";
import { useQueryClient } from "@tanstack/react-query";
import { countries } from "@/constants/countryCodes";
import { currencyCodes } from "@/constants/currencyCodes";
import { states } from "@/constants/states";
import { useToast } from "@/hooks/use-toast";
import { useCallout } from '@/hooks/use-callout';
import {newSupplierSchema, supplierSchema} from "@/schema/schema";
import { zodResolver } from "@hookform/resolvers/zod";

interface BaseAddress {
  addressLine0: string;
  addressLine1: string;
  addressLine2: string;
  postalZone: string;
  cityName: string;
  state: string;
  country: string;
}

interface AddressWithId extends BaseAddress {
  id: string;
}
interface SupplierData {
  code: string | null;
  id: string;
  name: string;
  tin: string;
  registrationType: string;
  registrationNumber: string;
  sstRegistrationNumber: string;
  tourismTaxRegistrationNumber: string | null;
  email: string;
  msicCode: string;
  businessActivityDescription: string;
  contactNumber: string;
  defaultCurrency: string | null;
  address: BaseAddress | AddressWithId;
}

const SupplierForm = ({
  initialData = null,
  onSubmit,
}: {
  initialData: SupplierData | null;
  onSubmit: (data: SupplierData) => void;
}) => {
  const isExisting = !!initialData;
  const schema = isExisting ? supplierSchema : newSupplierSchema;
  const form = useForm<SupplierData>({
    resolver:zodResolver(schema),
    defaultValues: initialData || {
      code: "",
      name: "",
      tin: "",
      registrationType: "",
      registrationNumber: "",
      sstRegistrationNumber: "",
      tourismTaxRegistrationNumber: "",
      email: "",
      msicCode: "",
      businessActivityDescription: "",
      contactNumber: "",
      defaultCurrency: "MYR",
      address: {
        addressLine0: "",
        addressLine1: "",
        addressLine2: "",
        postalZone: "",
        cityName: "",
        state: "",
        country: "MYS",
      },
    },
    mode: "onBlur",
  });

  const registrationTypes = [
    { value: "BRN", label: "BRN" },
    { value: "NRIC", label: "NRIC" },
    // { value: "ARMY", label: "ARMY" },
    { value: "PASSPORT", label: "PASSPORT" },
  ];

  return (
    <div className="space-y-6 pt-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            disabled={isExisting}
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-semibold">Code</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || undefined} className="h-10" maxLength={300} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold">
                  Name
                </FormLabel>
                <FormControl>
                  <Input {...field} className="h-10" maxLength={300} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-2">
            <FormField
              control={form.control}
              name="tin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">TIN</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-8" maxLength={14} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="registrationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">
                    Registration Type
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {registrationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="registrationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">
                    Reg No.
                  </FormLabel>
                  <FormControl>
                    <Input {...field} className="h-8" maxLength={20} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sstRegistrationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">SST Reg No.</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-8" maxLength={17} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      className="h-8"
                      maxLength={320}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">
                    Contact Number
                  </FormLabel>
                  <FormControl>
                    <Input {...field} className="h-8" maxLength={15} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <FormField
              control={form.control}
              name="msicCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">MSIC Code</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-8 shadow" maxLength={5} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="businessActivityDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-semibold">
                  Business Activity Description
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    className="min-h-[50px]"
                    maxLength={300}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="address.addressLine0"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-10" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="address.postalZone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.cityName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address.state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.Code} value={state.Code}>
                          {state.State}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address.country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.Code} value={country.Code}>
                          {country.Country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultCurrency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Currency</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? "Malaysian Ring"}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencyCodes.map((currency) => (
                        <SelectItem key={currency.Code} value={currency.Code}>
                          {currency.Currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              className="flex-1 h-14 bg-[#0F172A] text-base font-semibold">
              {initialData ? "Update Supplier" : "Register Supplier"}
            </Button>
            <SheetClose asChild>
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-14 text-base font-semibold"
              >
                Cancel
              </Button>
            </SheetClose>
          </div>
        </form>
      </Form>
    </div>
  );
};

// Separate the search functionality into a custom hook same as the item list
const useSupplierSearch = (suppliers: SupplierData[] | undefined | null) => {
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredSuppliers = React.useMemo(() => {
    if (!Array.isArray(suppliers)) {
      return [];
    };
    return suppliers.filter((supplier) =>
      Object.values(supplier).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [suppliers, searchTerm]);

  return { searchTerm, setSearchTerm, filteredSuppliers };
};


const SupplierList = () => {
  const { toast } = useToast();
  const { showNotification,hideNotification , CalloutNotification } = useCallout();
  const {
      suppliers,
      createSupplierAsync,
      updateSupplier,
      deleteSupplierAsync,
    } = useSuppliers();

  // console.table(suppliers?.data)
    
  const queryClient = useQueryClient();
  const [selectedSupplier, setSelectedSupplier] = React.useState<SupplierData | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const { searchTerm, setSearchTerm, filteredSuppliers } = useSupplierSearch(
    suppliers?.data
  );

  // console.table(filteredSuppliers)
  const cleanupBodyLock = () => {
    document.body.style.removeProperty('pointer-events');
    document.body.removeAttribute('data-scroll-locked');
    hideNotification();
  }
  const handleSubmit = async (data: Partial<SupplierData>) => {
    hideNotification();
    let success = false;
    let toastTitle = "";
    let toastMessage = "";
    try { 
      if (selectedSupplier) {
        await updateSupplier(data, {
          onSuccess: () => {
            success = true;
            queryClient.invalidateQueries({ queryKey: ["suppliers"] });
            toastTitle = "Update Successful"
            toastMessage = "The supplier has been successfully updated."
          },
          onError: (error) => {
            toast({
              title: "Error updating supplier",
              description: error.message,
              variant: "destructive"
            })
            // if (error?.response.status === 400) {
            //   const errorDetails = error.response.data.details;
            //   toastTitle = "Update Failed";
            //   // Show specific validation error messages
            //   if (errorDetails && Array.isArray(errorDetails)) {
            //     const errorMessages = errorDetails.map(detail => 
            //       `${detail.path}: ${detail.message}`
            //     ).join('\n');
            //     toastMessage = errorMessages;
            //   } else {
            //     toastMessage = error.response.data.message ||"Invalid supplier data provided"
            //     // Fallback for non-detailed 400 errors
            //     toast({
            //       title: "Update Failed",
            //       description: error.response.data.message || "Invalid supplier data provided",
            //       variant: "destructive",
            //       duration: 5000,
            //       icon: <CircleAlert className="text-white w-6 h-6"/>
            //     });
            //   }
            // } else {
            //   toastTitle = "Error"
            //   toastMessage = "Failed to update supplier. Please try again."
            // }
          }
        });
      } else {
        await createSupplierAsync(data as Omit<SupplierData, "id">, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["suppliers"] });
            success = true;
            toastTitle = "Supplier Registered"
            toastMessage = "The supplier has been successfully registered."
          },
          onError: (error) => {
            toast({
              title: "Error registering supplier",
              description: error.message,
              variant: "destructive",
            })
            // if (error.response?.status === 400) {
            //   const errorDetails = error.response.data.details;
            //   // Show specific validation error messages
            //   if (errorDetails && Array.isArray(errorDetails)) {
            //     const errorMessages = errorDetails.map(detail => 
            //       `${detail.path}: ${detail.message}`
            //     ).join('\n');
            //     toastTitle = "Validation Error"
            //     toastMessage = errorMessages;
            //   } else {
            //     // Fallback for non-detailed 400 errors
            //     toastTitle = "Update Failed"
            //     toastMessage = error.response.data.message || "Invalid supplier data provided";
            //   }
            // } else {
            //   toastTitle = "Error"
            //   toastMessage = "Failed to update supplier. Please try again.";
            // }
          }
        });
      }
    } catch (err) {
      console.error("Operation failed:", err);
    } finally {
      if (success) {
        setIsSheetOpen(false);
        setSelectedSupplier(null);
        cleanupBodyLock();
        toast({
          title: toastTitle,
          description: toastMessage,
          duration: 5000,
          variant:success?"default":"destructive",
          icon: success?<CheckCircle size={30} className="animate-spin-once text-green-400"/>:<CircleAlert size={30}/>,
        });
      }else{
        showNotification({
          title: toastTitle,
          description: toastMessage,
          variant: "destructive"
        });
      }
    }
  };


  const handleEdit = (supplier: Partial<SupplierData>) => {
    const supplierFormData: SupplierData = {
      code: supplier.code || "",
      id: supplier.id || "",
      name: supplier.name || "",
      tin: supplier.tin || "",
      registrationType: supplier.registrationType || "",
      registrationNumber: supplier.registrationNumber || "",
      sstRegistrationNumber: supplier.sstRegistrationNumber || "",
      tourismTaxRegistrationNumber: supplier.tourismTaxRegistrationNumber || "",
      email: supplier.email || "",
      msicCode: supplier.msicCode ||"",
      businessActivityDescription: supplier.businessActivityDescription || "",
      contactNumber: supplier.contactNumber || "",
      defaultCurrency: supplier.defaultCurrency || "MYR",
      address: {
        addressLine0: supplier?.address?.addressLine0 || "",
        addressLine1: supplier?.address?.addressLine1 || "",
        addressLine2: supplier?.address?.addressLine2 || "",
        postalZone: supplier?.address?.postalZone || "",
        cityName: supplier?.address?.cityName || "",
        state: supplier?.address?.state || "",
        country: "MYS",
      },
    }

    hideNotification();
    setSelectedSupplier(supplierFormData);
    setIsSheetOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSupplierAsync(id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["suppliers"] });
          toast({
            title: "Success",
            description: "Supplier has been deleted.",
            duration: 5000,
            icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
          });
        },
      });
    } catch (err) {
      console.error("Delete operation failed:", err);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 w-[300px] shadow-sm"
            />
          </div>
        </div>
        <Sheet open={isSheetOpen} onOpenChange={(open) => {
            setIsSheetOpen(open);
            // Remove pointer-events style and data-scroll-locked attribute when sheet closes
            if (!open) {
              document.body.style.removeProperty('pointer-events');
              document.body.removeAttribute('data-scroll-locked');
            }
          }}>
          <SheetTrigger asChild>
            <Button onClick={() => setSelectedSupplier(null)}>
              <Plus className="mr-2 h-4 w-4" />
              Register Supplier
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[800px] sm:w-[800px] overflow-y-auto"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              cleanupBodyLock();
            }}>
            <SheetHeader>
              <SheetTitle>
                {selectedSupplier ? "Edit Supplier" : "Register New Supplier"}
              </SheetTitle>
              <SheetDescription>
                {selectedSupplier
                  ? "Edit the supplier details below."
                  : "Fill in the supplier details below."}
              </SheetDescription>
            </SheetHeader>
            <SupplierForm
              initialData={selectedSupplier}
              onSubmit={handleSubmit}
            />
            <div className="space-y-6 pt-6">
              <CalloutNotification />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <Table>
        <TableHeader className="bg-slate-50 dark:bg-slate-800">
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Registration Type</TableHead>
            <TableHead>Registration No.</TableHead>
            <TableHead>SST No.</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead className="w-[50px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredSuppliers.map((supplier) => (
            <TableRow key={supplier.id}>
              <TableCell className="font-medium">{supplier.code}</TableCell>
              <TableCell className="font-medium">{supplier.name}</TableCell>
              <TableCell>{supplier.registrationType}</TableCell>
              <TableCell>{supplier.registrationNumber}</TableCell>
              <TableCell>{supplier.sstRegistrationNumber}</TableCell>
              <TableCell>{supplier.contactNumber}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(supplier)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(supplier.id)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SupplierList;
